<script lang="ts">
  // Square button with only an icon. `label` is required: it is the tooltip
  // and the accessible name.
  //   <IconButton label={t('common.delete')} variant="danger"><Trash2 /></IconButton>
  import type { Snippet } from 'svelte';
  import type { HTMLButtonAttributes } from 'svelte/elements';
  import { tooltip } from './actions';

  interface Props extends HTMLButtonAttributes {
    label: string;
    size?: 'xs' | 'sm' | 'md' | 'lg';
    variant?: 'ghost' | 'secondary' | 'primary' | 'danger';
    /** Pill/circle instead of the rounded square. */
    round?: boolean;
    active?: boolean;
    href?: string;
    children: Snippet;
  }
  let { label, size = 'md', variant = 'ghost', round = false, active = false, href, children, class: cls = '', type = 'button', ...rest }: Props = $props();
</script>

{#if href}
  <a {href} class="ib ib-{size} ib-{variant} {cls}" class:round class:active use:tooltip={label} aria-label={label}>{@render children()}</a>
{:else}
  <button {type} class="ib ib-{size} ib-{variant} {cls}" class:round class:active use:tooltip={label} aria-label={label} aria-pressed={active || undefined} {...rest}>{@render children()}</button>
{/if}

<style>
  .ib {
    display: inline-grid;
    place-items: center;
    width: var(--control-h);
    height: var(--control-h);
    padding: 0;
    border-radius: var(--radius-sm);
    border: 1px solid transparent;
    background: transparent;
    color: var(--text-2);
    transition: background var(--dur), color var(--dur), border-color var(--dur);
    flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
  }
  .ib:focus-visible { outline: none; box-shadow: var(--ring); }
  .ib :global(svg) { width: var(--icon-lg); height: var(--icon-lg); }
  .ib-sm { width: var(--control-h-sm); height: var(--control-h-sm); border-radius: var(--radius-xs); }
  .ib-sm :global(svg) { width: var(--icon-md); height: var(--icon-md); }
  .ib-xs { width: 24px; height: 24px; border-radius: var(--radius-xs); }
  .ib-xs :global(svg) { width: var(--icon-sm); height: var(--icon-sm); }
  .ib-lg { width: var(--control-h-lg); height: var(--control-h-lg); }
  .ib-lg :global(svg) { width: var(--icon-xl); height: var(--icon-xl); }
  .round { border-radius: var(--radius-full); }
  .ib-ghost:hover { background: var(--surface-hover); color: var(--text); }
  .ib-secondary { background: var(--surface); border-color: var(--border); }
  .ib-secondary:hover { background: var(--surface-2); color: var(--text); border-color: var(--border-strong); }
  .ib-primary { background: var(--brand); color: var(--on-brand); }
  .ib-primary:hover { background: var(--brand-strong); }
  .ib-danger:hover { background: var(--danger-soft); color: var(--danger); }
  .active { background: var(--brand-soft); color: var(--brand); }
  .ib:disabled { opacity: 0.45; pointer-events: none; }
</style>
